import { GAME_NAME } from '@cloudpearl/config';
import type { UiTheme } from '@cloudpearl/config';
import { ICONS } from './icons';

export type RunMode = 'race' | 'coop';

/** Shared-run room as the UI sees it (null = solo). */
export interface RoomInfo {
  code: string;
  /** 'hosting' = waiting for a friend; 'connecting' = joining; 'connected' = both here. */
  status: 'hosting' | 'connecting' | 'connected';
  mode: RunMode;
  /** The friend has the same world loaded and is on the menu. */
  friendReady: boolean;
}

export interface MenuInfo {
  mapName: string;
  isDaily: boolean;
  best: number | null;
  beat: number | null;
  isTouch: boolean;
  room: RoomInfo | null;
}

export interface CrashInfo {
  distance: number;
  best: number;
  newBest: boolean;
  cause: string;
  perfects: number;
  closeCalls: number;
  topSpeedKmh: number;
  beat: number | null;
}

/** End of a shared run. */
export interface ResultInfo {
  mode: RunMode;
  you: number;
  friend: number;
  /** Co-op: the furthest either of you reached. */
  team: number;
}

export interface SettingsState {
  sound: boolean;
  music: boolean;
  quality: 'auto' | 'low' | 'medium' | 'high';
  speedFx: boolean;
  /** Render at most ~60 fps on high-refresh displays (cooler, quieter, longer battery). */
  cap60: boolean;
  /** Your pearl's colour (hex); a friend sees it too. */
  pearl: string;
}

/** The pearl colours on offer — soft nacre tones that read on sand and turf alike. */
export const PEARL_COLORS: { name: string; hex: string }[] = [
  { name: 'Pearl', hex: '#fdfaf6' },
  { name: 'Blush', hex: '#ffd6dc' },
  { name: 'Peach', hex: '#ffd9b8' },
  { name: 'Gold', hex: '#f6dc9c' },
  { name: 'Mint', hex: '#c8f0da' },
  { name: 'Sky', hex: '#bfd6ff' },
  { name: 'Lilac', hex: '#ddcdff' },
  { name: 'Tahitian', hex: '#7d8796' },
];

/** Room codes are 6 characters, shown split in two: ABC-DEF. */
export const formatRoomCode = (code: string): string => (code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code);

export interface UiHandlers {
  onStart(): void;
  onMap(dir: -1 | 1): void;
  onNewSeed(): void;
  onDaily(): void;
  onSeedEntered(code: string): void;
  onCopyLink(kind: 'world' | 'challenge'): void;
  onSettings(next: SettingsState): void;
  onPause(): void;
  onResume(): void;
  onMenu(): void;
  onRetry(): void;
  onHost(): void;
  onJoin(code: string): void;
  onLeave(): void;
  onMode(mode: RunMode): void;
  sfx(kind: 'uiMove' | 'uiSelect' | 'uiBack'): void;
}

const fmt = (n: number): string => Math.floor(n).toLocaleString('en-US');
const h = (html: string): HTMLElement => {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild as HTMLElement;
};
const MODE_NOTE: Record<RunMode, string> = {
  race: 'Same world, side by side. Furthest run wins.',
  coop: 'Fall and you respawn on your friend. Go as far as you can together.',
};

/**
 * DOM overlay. Every element has to earn its place: the menu is the map, one caption and one
 * dock (Run + a few icons); during play the only permanent element is the distance.
 */
export class Ui {
  readonly root: HTMLElement;
  private readonly menu: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly crash: HTMLElement;
  private readonly pause: HTMLElement;
  private readonly settings: HTMLElement;
  private readonly room: HTMLElement;
  private readonly veil: HTMLElement;
  private readonly toastEl: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly distEl: HTMLElement;
  private readonly subEl: HTMLElement;
  private readonly noteEl: HTMLElement;
  private readonly callouts: HTMLElement;
  private settingsState: SettingsState;
  private lastDist = -1;
  private handlers: UiHandlers;
  private crashReadyAt = 0;
  private hintTimer = 0;
  private roomInfo: RoomInfo | null = null;

  constructor(parent: HTMLElement, handlers: UiHandlers, settings: SettingsState) {
    this.handlers = handlers;
    this.settingsState = { ...settings };
    this.root = h(`<div class="ui"></div>`);
    parent.appendChild(this.root);

    this.menu = h(`
      <section class="menu" data-open="false">
        <header class="menu-top">
          <h1 class="brand">${ICONS.signal}<span>${GAME_NAME}</span></h1>
          <button class="icon-btn glass" data-act="settings" aria-label="Settings" data-tip="Settings" data-tip-pos="below">${ICONS.gear}</button>
        </header>
        <footer class="menu-bottom">
          <button class="room-bar glass" data-act="together">
            <i class="dot"></i><span class="room-status"></span><span class="room-mode"></span>
          </button>
          <div class="map-picker">
            <button class="icon-btn ghost" data-act="prev" aria-label="Previous map">${ICONS.left}</button>
            <div class="map-name"></div>
            <button class="icon-btn ghost" data-act="next" aria-label="Next map">${ICONS.right}</button>
          </div>
          <div class="map-sub"></div>
          <div class="dock glass">
            <button class="run-btn" data-act="start">${ICONS.play}<span class="run-t">Run</span><kbd class="key">Space</kbd></button>
            <button class="icon-btn small" data-act="copy-link" aria-label="Copy link" data-tip="Copy link">${ICONS.link}</button>
            <button class="icon-btn small daily" data-act="daily" aria-label="Daily map" data-tip="Daily map">${ICONS.calendar}</button>
            <button class="icon-btn small" data-act="dice" aria-label="Random map" data-tip="Random map">${ICONS.dice}</button>
            <button class="icon-btn small together" data-act="together" aria-label="Play with a friend" data-tip="Play with a friend">${ICONS.people}</button>
          </div>
        </footer>
      </section>`);

    this.hud = h(`
      <section class="hud" data-open="false">
        <div class="north" aria-hidden="true"><div class="needle"><svg viewBox="0 0 24 24"><path d="M12 3.5 18 16.5 12 13.6 6 16.5Z"/></svg></div><span>N</span></div>
        <div class="dist"><span class="dist-n">0</span><span class="dist-u">m</span></div>
        <div class="sub"></div>
        <div class="callouts"></div>
        <button class="icon-btn glass pause-btn" data-act="pause" aria-label="Pause">${ICONS.pause}</button>
        <div class="hint glass"></div>
        <div class="note glass"></div>
      </section>`);

    this.crash = h(`
      <section class="crash" data-open="false">
        <div class="card"></div>
        <div class="crash-actions">
          <button class="icon-btn glass" data-act="menu" aria-label="Menu" data-tip="Menu">${ICONS.home}</button>
          <button class="run-btn" data-act="start">${ICONS.retry}<span class="run-t">Run again</span><kbd class="key">Space</kbd></button>
          <button class="icon-btn glass share" data-act="share-challenge" aria-label="Copy challenge link" data-tip="Copy challenge link">${ICONS.link}</button>
        </div>
      </section>`);

    this.pause = h(`
      <section class="pause" data-open="false">
        <div class="card">
          <div class="card-t">Paused</div>
          <div class="pause-actions">
            <button class="run-btn" data-act="resume">${ICONS.play}<span class="run-t">Resume</span></button>
            <button class="icon-btn glass" data-act="retry" aria-label="Restart" data-tip="Restart">${ICONS.retry}</button>
            <button class="icon-btn glass" data-act="menu" aria-label="Menu" data-tip="Menu">${ICONS.home}</button>
          </div>
        </div>
      </section>`);

    this.settings = h(`
      <section class="sheet settings" data-open="false">
        <div class="card">
          <div class="sheet-head"><span>Settings</span><button class="icon-btn small" data-act="close-sheet" aria-label="Close">${ICONS.close}</button></div>
          <div class="row"><span>Pearl</span><div class="swatches">${PEARL_COLORS.map((p) => `<button class="swatch" data-v="${p.hex}" style="--c:${p.hex}" aria-label="${p.name}" data-tip="${p.name}"></button>`).join('')}</div></div>
          <div class="row"><span>Sound effects</span><button class="toggle" data-set="sound"></button></div>
          <div class="row"><span>Music</span><button class="toggle" data-set="music"></button></div>
          <div class="row"><span>Speed effects</span><button class="toggle" data-set="speedFx"></button></div>
          <div class="row"><span>Limit to 60 fps</span><button class="toggle" data-set="cap60"></button></div>
          <div class="row"><span>Quality</span><div class="seg" data-set="quality"><button data-v="auto">Auto</button><button data-v="low">Low</button><button data-v="medium">Med</button><button data-v="high">High</button></div></div>
          <div class="row"><span>World code</span><form class="seed-form"><input class="seed-input" maxlength="40" spellcheck="false" autocomplete="off" placeholder="AMBER-FOX-42"/><button class="pill" type="submit">Go</button></form></div>
          <div class="sheet-foot">A / D steer · hold Space to dive · Esc pause</div>
        </div>
      </section>`);

    this.room = h(`
      <section class="sheet room" data-open="false" data-view="start">
        <div class="card">
          <div class="sheet-head"><span>Play together</span><button class="icon-btn small" data-act="close-sheet" aria-label="Close">${ICONS.close}</button></div>
          <div class="v-start">
            <button class="run-btn wide" data-act="host"><span class="run-t">Host a room</span></button>
            <div class="or"><span>or join one</span></div>
            <form class="join-form">
              <div class="code-slots" role="group" aria-label="Room code">${[0, 1, 2].map(() => '<input class="slot" spellcheck="false" autocomplete="off" autocapitalize="characters" aria-label="Room code character"/>').join('')}<span class="dash">–</span>${[0, 1, 2].map(() => '<input class="slot" spellcheck="false" autocomplete="off" autocapitalize="characters" aria-label="Room code character"/>').join('')}</div>
              <button class="pill" type="submit">Join</button>
            </form>
          </div>
          <div class="v-room">
            <div class="room-code"></div>
            <div class="room-line"><i class="dot"></i><span class="room-status"></span></div>
            <div class="row"><span>Mode</span><div class="seg" data-set="mode"><button data-v="race">Race</button><button data-v="coop">Co-op</button></div></div>
            <p class="mode-note"></p>
            <div class="sheet-actions"><button class="pill" data-act="copy-room">${ICONS.link}<span>Copy invite</span></button><button class="pill ghost" data-act="leave">Leave</button></div>
          </div>
        </div>
      </section>`);

    this.veil = h(`<div class="veil" data-open="true" data-brand="true"><div class="veil-inner"><div class="veil-mark">${ICONS.signal}</div><div class="veil-title">${GAME_NAME}</div><div class="veil-dots"><i></i><i></i><i></i></div></div></div>`);
    this.toastEl = h(`<div class="toast"></div>`);
    this.countEl = h(`<div class="countdown"></div>`);

    this.root.append(this.menu, this.hud, this.crash, this.pause, this.settings, this.room, this.countEl, this.veil, this.toastEl);
    this.distEl = this.hud.querySelector('.dist-n')!;
    this.subEl = this.hud.querySelector('.sub')!;
    this.noteEl = this.hud.querySelector('.note')!;
    this.callouts = this.hud.querySelector('.callouts')!;
    this.bind();
    this.renderSettings();
  }

  private bind(): void {
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      // A click on a sheet's backdrop closes it.
      if (t.classList.contains('sheet')) {
        this.closeSheets();
        return;
      }
      const el = t.closest<HTMLElement>('[data-act]');
      if (!el) return;
      e.stopPropagation();
      // Never leave focus on a button: Space is the dive key, and would "click" it again.
      el.blur();
      const H = this.handlers;
      switch (el.dataset['act']) {
        case 'start':
          H.onStart();
          break;
        case 'prev':
          H.sfx('uiMove');
          H.onMap(-1);
          break;
        case 'next':
          H.sfx('uiMove');
          H.onMap(1);
          break;
        case 'dice':
          H.sfx('uiSelect');
          H.onNewSeed();
          break;
        case 'daily':
          H.sfx('uiSelect');
          H.onDaily();
          break;
        case 'copy-link':
        case 'copy-room':
          H.sfx('uiSelect');
          H.onCopyLink('world');
          break;
        case 'share-challenge':
          H.sfx('uiSelect');
          H.onCopyLink('challenge');
          break;
        case 'settings':
          H.sfx('uiSelect');
          this.renderSettings();
          this.settings.dataset['open'] = 'true';
          break;
        case 'together':
          H.sfx('uiSelect');
          this.room.dataset['open'] = 'true';
          if (!this.roomInfo && !this.root.closest('[data-touch="true"]')) setTimeout(() => this.room.querySelector<HTMLInputElement>('.slot')?.focus({ preventScroll: true }), 80);
          break;
        case 'close-sheet':
          H.sfx('uiBack');
          this.closeSheets();
          break;
        case 'host':
          H.sfx('uiSelect');
          H.onHost();
          break;
        case 'leave':
          H.sfx('uiBack');
          H.onLeave();
          break;
        case 'pause':
          H.onPause();
          break;
        case 'resume':
          H.sfx('uiSelect');
          H.onResume();
          break;
        case 'retry':
          H.sfx('uiSelect');
          H.onRetry();
          break;
        case 'menu':
          H.sfx('uiBack');
          H.onMenu();
          break;
      }
    });
    // Block pointerdown on interactive UI so it never reaches the play surface.
    this.root.addEventListener('pointerdown', (e) => {
      if ((e.target as HTMLElement).closest('button, input, form, .card, .dock')) e.stopPropagation();
    });
    for (const sheet of [this.settings, this.room]) {
      sheet.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        const sw = t.closest<HTMLElement>('.swatch');
        if (sw) {
          this.settingsState.pearl = sw.dataset['v']!;
          this.handlers.sfx('uiMove');
          this.commitSettings();
          return;
        }
        const tog = t.closest<HTMLElement>('.toggle');
        if (tog) {
          const key = tog.dataset['set'] as 'sound' | 'music' | 'speedFx' | 'cap60';
          this.settingsState[key] = !this.settingsState[key];
          this.handlers.sfx('uiSelect');
          this.commitSettings();
          return;
        }
        const segBtn = t.closest<HTMLElement>('.seg button');
        if (segBtn) {
          const key = segBtn.parentElement!.dataset['set'];
          this.handlers.sfx('uiMove');
          if (key === 'mode') this.handlers.onMode(segBtn.dataset['v'] as RunMode);
          else {
            this.settingsState.quality = segBtn.dataset['v'] as SettingsState['quality'];
            this.commitSettings();
          }
        }
      });
    }
    this.settings.querySelector<HTMLFormElement>('.seed-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = this.settings.querySelector<HTMLInputElement>('.seed-input')!.value;
      if (v.trim()) {
        this.handlers.onSeedEntered(v);
        this.closeSheets();
      }
    });
    // Six single-character slots: typing advances, backspace steps back, a pasted code fills them.
    const slots = Array.from(this.room.querySelectorAll<HTMLInputElement>('.slot'));
    const code = (): string => slots.map((s) => s.value).join('');
    const submit = (): void => {
      if (code().length !== 6) return;
      this.handlers.sfx('uiSelect');
      this.handlers.onJoin(code());
      (document.activeElement as HTMLElement | null)?.blur();
    };
    slots.forEach((slot, i) => {
      slot.addEventListener('input', () => {
        const v = slot.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (v.length > 1) {
          // Pasted / autofilled: spread across this and the following slots.
          for (let k = 0; k < v.length && i + k < slots.length; k++) slots[i + k]!.value = v[k]!;
          slots[Math.min(slots.length - 1, i + v.length)]!.focus();
        } else {
          slot.value = v;
          if (v && i < slots.length - 1) slots[i + 1]!.focus();
        }
        if (code().length === 6) submit();
      });
      slot.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !slot.value && i > 0) {
          slots[i - 1]!.value = '';
          slots[i - 1]!.focus();
          e.preventDefault();
        } else if (e.key === 'ArrowLeft' && i > 0) slots[i - 1]!.focus();
        else if (e.key === 'ArrowRight' && i < slots.length - 1) slots[i + 1]!.focus();
        // Typing over a filled slot replaces it.
        else if (e.key.length === 1 && slot.value) slot.value = '';
      });
      slot.addEventListener('focus', () => slot.select());
    });
    this.room.querySelector<HTMLFormElement>('.join-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      submit();
    });
  }

  /** Any sheet (settings / play together) is open — Space and taps don't start a run. */
  get settingsOpen(): boolean {
    return this.settings.dataset['open'] === 'true' || this.room.dataset['open'] === 'true';
  }

  closeSheets(): void {
    this.settings.dataset['open'] = 'false';
    this.room.dataset['open'] = 'false';
    (document.activeElement as HTMLElement | null)?.blur();
  }

  closeSettings(): void {
    this.closeSheets();
  }

  private commitSettings(): void {
    this.renderSettings();
    this.handlers.onSettings({ ...this.settingsState });
  }

  private renderSettings(): void {
    for (const t of this.settings.querySelectorAll<HTMLElement>('.toggle')) {
      const k = t.dataset['set'] as 'sound' | 'music' | 'speedFx' | 'cap60';
      t.dataset['on'] = String(this.settingsState[k]);
      t.setAttribute('aria-pressed', String(this.settingsState[k]));
    }
    for (const b of this.settings.querySelectorAll<HTMLElement>('.seg[data-set="quality"] button')) b.dataset['on'] = String(b.dataset['v'] === this.settingsState.quality);
    for (const b of this.settings.querySelectorAll<HTMLElement>('.swatch')) b.dataset['on'] = String(b.dataset['v'] === this.settingsState.pearl);
  }

  setSettings(s: SettingsState): void {
    this.settingsState = { ...s };
    this.renderSettings();
  }

  setTheme(t: UiTheme, mapId: string): void {
    const r = this.root.style;
    r.setProperty('--ink', t.ink);
    r.setProperty('--ink-soft', t.inkSoft);
    r.setProperty('--paper', t.paper);
    r.setProperty('--glow', t.glow);
    this.root.dataset['map'] = mapId;
  }

  // ------------------------------------------------------------------ menu & room

  showMenu(info: MenuInfo): void {
    this.updateMenu(info);
    this.menu.dataset['open'] = 'true';
    this.hud.dataset['open'] = 'false';
    this.crash.dataset['open'] = 'false';
    this.pause.dataset['open'] = 'false';
  }

  updateMenu(info: MenuInfo): void {
    const q = <T extends HTMLElement>(s: string): T => this.menu.querySelector<T>(s)!;
    q('.map-name').textContent = info.mapName;
    this.root.dataset['touch'] = String(info.isTouch);
    q('.map-sub').innerHTML = info.beat ? `beat <b>${fmt(info.beat)} m</b>` : info.best ? `best <b>${fmt(info.best)} m</b>` : '';
    q('.daily').dataset['on'] = String(info.isDaily);
    this.setRoom(info.room);
  }

  /** Room state for the menu bar, the dock and the room sheet. */
  setRoom(room: RoomInfo | null): void {
    this.roomInfo = room;
    this.root.dataset['room'] = room ? room.status : 'none';
    const sheet = this.room;
    sheet.dataset['view'] = room ? 'room' : 'start';
    const runBtn = this.menu.querySelector<HTMLButtonElement>('.run-btn')!;
    const runT = runBtn.querySelector('.run-t')!;
    if (!room) {
      for (const s of sheet.querySelectorAll<HTMLInputElement>('.slot')) s.value = '';
      runBtn.disabled = false;
      runT.textContent = 'Run';
      return;
    }
    const status = room.status === 'hosting' ? 'Waiting for a friend…' : room.status === 'connecting' ? 'Joining…' : room.friendReady ? 'Friend is here' : 'Friend is loading…';
    sheet.querySelector('.room-code')!.textContent = formatRoomCode(room.code);
    for (const el of this.root.querySelectorAll('.room-status')) el.textContent = status;
    this.menu.querySelector('.room-mode')!.textContent = room.mode === 'race' ? 'Race' : 'Co-op';
    sheet.querySelector('.mode-note')!.textContent = MODE_NOTE[room.mode];
    for (const b of sheet.querySelectorAll<HTMLElement>('.seg[data-set="mode"] button')) b.dataset['on'] = String(b.dataset['v'] === room.mode);
    // A shared run starts once you're both on the menu with the same world loaded.
    const waiting = room.status === 'connected' && !room.friendReady;
    runBtn.disabled = waiting;
    runT.textContent = waiting ? 'Waiting' : 'Run';
  }

  openRoomSheet(open: boolean): void {
    this.room.dataset['open'] = String(open);
  }

  // ------------------------------------------------------------------ HUD

  /** @param hint one line of controls shown for a few seconds (first runs), or null */
  showHud(isTouch: boolean, hint: string | null): void {
    this.menu.dataset['open'] = 'false';
    this.crash.dataset['open'] = 'false';
    this.pause.dataset['open'] = 'false';
    this.hud.dataset['open'] = 'true';
    this.hud.dataset['touch'] = String(isTouch);
    this.lastDist = -1;
    this.subEl.textContent = '';
    this.callouts.textContent = '';
    this.note(null);
    const hintEl = this.hud.querySelector<HTMLElement>('.hint')!;
    hintEl.textContent = hint ?? '';
    this.hud.dataset['hint'] = String(!!hint);
    clearTimeout(this.hintTimer);
    if (hint) this.hintTimer = window.setTimeout(() => (this.hud.dataset['hint'] = 'false'), 4500);
  }

  setDistance(m: number, best: number | null): void {
    const v = Math.floor(m);
    if (v === this.lastDist) return;
    this.lastDist = v;
    this.distEl.textContent = fmt(v);
    if (this.roomInfo) return;
    const left = best && best > 100 ? best - v : 0;
    this.subEl.textContent = left > 0 && left < 300 ? `best in ${fmt(left)} m` : '';
  }

  /** Shared run: one line under the distance about your friend. */
  setFriend(text: string): void {
    if (this.subEl.textContent !== text) this.subEl.textContent = text;
  }

  /** A short note during play (respawning, waiting for your friend…), or null. */
  note(text: string | null): void {
    if (this.noteEl.textContent !== (text ?? '')) this.noteEl.textContent = text ?? '';
    this.hud.dataset['note'] = String(!!text);
  }

  /** North pointer: shown only when you're heading well away from north. */
  setNorth(angle: number): void {
    // Called every frame: touch the DOM only when something visible changes (each style write
    // forces a style recalc).
    const off = Math.abs(angle) > 0.5;
    if (!this.northEl) this.northEl = this.hud.querySelector('.north');
    const el = this.northEl!;
    if (off !== this.northOn) {
      this.northOn = off;
      el.dataset['on'] = String(off);
    }
    const a = Math.round(angle * 200) / 200;
    if (a !== this.northA) {
      this.northA = a;
      el.style.setProperty('--a', `${a}rad`);
    }
  }
  private northEl: HTMLElement | null = null;
  private northOn: boolean | null = null;
  private northA = NaN;

  /** Short-lived callout ("Perfect ×3", "Close call", "Wind gate", "New best", "Bump"). */
  callout(text: string, kind: 'perfect' | 'close' | 'best' | 'gate' | 'bump', sub?: string): void {
    const el = h(`<div class="callout ${kind}"><span class="ct">${text}</span>${sub ? `<span class="cs">${sub}</span>` : ''}</div>`);
    this.callouts.appendChild(el);
    while (this.callouts.childElementCount > 2) this.callouts.firstElementChild!.remove();
    setTimeout(() => el.remove(), kind === 'best' ? 1600 : 1000);
  }

  /** 3 · 2 · 1 · Go before a shared run; null hides it. */
  countdown(n: number | null): void {
    this.countEl.textContent = n === null ? '' : n > 0 ? String(n) : 'Go';
    this.countEl.dataset['on'] = String(n !== null);
    if (n !== null) {
      this.countEl.classList.remove('tick');
      void this.countEl.offsetWidth;
      this.countEl.classList.add('tick');
    }
  }

  // ------------------------------------------------------------------ end of run

  showCrash(info: CrashInfo): void {
    const best = info.newBest ? `<span class="star">${ICONS.star}</span>New best` : `Best ${fmt(info.best)} m`;
    const beat = info.beat ? `<div class="beat-note">${info.distance > info.beat ? 'Challenge beaten' : `${fmt(info.beat - info.distance)} m short of the challenge`}</div>` : '';
    this.openCard(
      `<div class="cause">${info.cause}</div>
       <div class="big"><span class="big-n">${fmt(info.distance)}</span><span class="big-u">m</span></div>
       <div class="best-line" data-new="${info.newBest}">${best}</div>
       ${beat}
       <div class="stats"><span><b>${info.perfects}</b> perfect</span><span><b>${info.closeCalls}</b> close</span><span><b>${info.topSpeedKmh}</b> km/h</span></div>`,
      'Run again',
      true,
    );
  }

  /** The shared run is over for both of you. */
  showResult(r: ResultInfo): void {
    const row = (who: string, m: number, lead: boolean): string => `<div class="res-row" data-lead="${lead}"><span>${who}</span><b>${fmt(m)} m</b></div>`;
    const title = r.mode === 'coop' ? 'Together' : r.you === r.friend ? 'Dead heat' : r.you > r.friend ? 'You win' : 'Friend wins';
    const big = r.mode === 'coop' ? r.team : Math.max(r.you, r.friend);
    this.openCard(
      `<div class="cause">${title}</div>
       <div class="big"><span class="big-n">${fmt(big)}</span><span class="big-u">m</span></div>
       <div class="res">${row('You', r.you, r.you >= r.friend)}${row('Friend', r.friend, r.friend > r.you)}</div>`,
      'Rematch',
      false,
    );
  }

  private openCard(html: string, primary: string, share: boolean): void {
    this.hud.dataset['open'] = 'false';
    this.crash.querySelector('.card')!.innerHTML = html;
    this.crash.querySelector('.crash-actions .run-t')!.textContent = primary;
    this.crash.querySelector<HTMLElement>('.share')!.hidden = !share;
    this.crash.dataset['open'] = 'true';
    this.crash.dataset['ready'] = 'false';
    this.crashReadyAt = performance.now() + 550;
    setTimeout(() => (this.crash.dataset['ready'] = 'true'), 550);
  }

  /** Avoid an accidental retry from a panicked tap the instant you crash. */
  get crashReady(): boolean {
    return performance.now() >= this.crashReadyAt;
  }

  showPause(open: boolean): void {
    this.pause.dataset['open'] = String(open);
  }

  hideAll(): void {
    for (const el of [this.menu, this.hud, this.crash, this.pause]) el.dataset['open'] = 'false';
  }

  /** Loading veil; the brand mark shows only when the title is the game's name. */
  veilOn(on: boolean, title?: string): void {
    if (title) {
      this.veil.querySelector('.veil-title')!.textContent = title;
      this.veil.dataset['brand'] = String(title === GAME_NAME);
    }
    this.veil.dataset['open'] = String(on);
  }

  toast(text: string): void {
    this.toastEl.textContent = text;
    this.toastEl.dataset['open'] = 'true';
    clearTimeout((this.toastEl as unknown as { _t?: number })._t);
    (this.toastEl as unknown as { _t?: number })._t = window.setTimeout(() => (this.toastEl.dataset['open'] = 'false'), 1800);
  }
}
